import { FormulaLiteModel } from './formula.model';

export enum TriggerOperator {
    Equals = 'equals',
    NotEquals = 'not_equals',
    GreaterThan = 'greater_than',
    LessThan = 'less_than',
    GreaterThanOrEquals = 'greater_than_equals',
    LessThanOrEquals = 'less_than_equals',
}

export const DEFAULT_LOOKBACKS: number[] = [1, 7, 30];

export const SUB_TRIGGER_ID_SLUG = 'NEW_SUB_TRIGGER';
export const TRIGGER_ID_SLUG = 'NEW_TRIGGER';

export const DEFAULT_VALUES = { value: 0, lookback: DEFAULT_LOOKBACKS[0] };

export interface CompoundTriggerModel {
    id: string;
    name: string;
    description?: string;
    exit_event_id?: number;
    created_at: string;
    archived_at?: string;
    trigger_type: CompoundTriggerType;
    delay_period?: CompoundTriggerDelayPeriod;
    delay_minutes?: number;
    repeat_entry?: CompoundTriggerRepeatEntry | null;
    /** `null` for aampe_optimised triggers, which ignore it. */
    realtime_state?: RealtimeTriggerState | null;
}

export interface CompoundTriggerCatalog extends CompoundTriggerModel {
    message_count: number;
}

export interface SubTriggerModel {
    id: string;
    contact_event_id: number;
    operator: TriggerOperator;
    value: number;
    lookback: number;
}

export interface TriggerModel {
    id: string;
    contact_event_id: number;
    is_exit_event?: boolean;
}

export interface CompoundTriggerDetailModel extends CompoundTriggerModel {
    sub_triggers: SubTriggerModel[];
    triggers: TriggerModel[];
    formulas: FormulaLiteModel[];
    message_count: number;
    /** Start of the current live period; `null` unless live. */
    live_started_at?: string | null;
    /** When the set of entry / exit events last changed. */
    definition_changed_at?: string | null;
}

export interface SubTriggerCreateUpdateModel {
    id?: string;
    contact_event_id?: number;
    operator?: TriggerOperator;
    value?: number;
    lookback?: number;
}

export interface TriggerCreateUpdateModel {
    id?: string;
    contact_event_id?: number;
    is_exit_event?: boolean;
}

export interface CompoundTriggerUpdate {
    name?: string;
    description?: string;
    exit_event_id?: number;
    sub_triggers?: SubTriggerCreateUpdateModel[];
    triggers?: TriggerCreateUpdateModel[];
    trigger_type?: CompoundTriggerType;
    delay_period?: CompoundTriggerDelayPeriod;
    delay_minutes?: number;
    repeat_entry?: CompoundTriggerRepeatEntry | null;
}

export interface CompoundTriggerCreate {
    name: string;
    description?: string;
    exit_event_id?: number;
    sub_triggers: SubTriggerCreateUpdateModel[];
    triggers: TriggerCreateUpdateModel[];
    trigger_type: CompoundTriggerType;
    delay_period?: CompoundTriggerDelayPeriod;
    delay_minutes?: number;
    repeat_entry?: CompoundTriggerRepeatEntry | null;
}

export enum CompoundTriggerType {
    AAMPE_OPTIMISED = 'aampe_optimised',
    SEND_IMMEDIATELY = 'send_immediately',
    SEND_WITH_DELAY = 'send_with_delay',
}

export const REALTIME_TRIGGER_TYPES: CompoundTriggerType[] = [
    CompoundTriggerType.SEND_IMMEDIATELY,
    CompoundTriggerType.SEND_WITH_DELAY,
];

export const isRealtimeTriggerType = (type?: CompoundTriggerType | null) =>
    !!type && REALTIME_TRIGGER_TYPES.includes(type);

export const TriggerDisplayNames: Record<CompoundTriggerType, string> = {
    [CompoundTriggerType.AAMPE_OPTIMISED]: 'Aampe optimised',
    [CompoundTriggerType.SEND_IMMEDIATELY]: 'Send immediately',
    [CompoundTriggerType.SEND_WITH_DELAY]: 'Send with delay',
};

export enum CompoundTriggerDelayPeriod {
    DAYS = 'days',
    HOURS = 'hours',
    MINS = 'mins',
    NoneValue = 'None',
}

export const EXIT_SUB_TRIGGER_DESCRIPTION =
    'Stop sending trigger messages for a user if:';
export const SUB_TRIGGER_DESCRIPTION =
    'Start triggering messages for a user if:';
export const RT_TRIGGER_DELAY_PERIOD_DESCRIPTION =
    'Once the trigger event is met, send the messages after';
export const RT_TRIGGER_EXIT_DESCRIPTION =
    'Unless the following exception event occurs:';

/**
 * What a repeat entry event does while a send_with_delay trigger's delay is
 * still running. Only applies to send_with_delay: send_immediately has no delay
 * and the backend stores `null` for it. `null` on send_with_delay means
 * RESTART_DELAY.
 */
export enum CompoundTriggerRepeatEntry {
    RESTART_DELAY = 'restart_delay',
    KEEP_FIRST = 'keep_first',
}

export const DEFAULT_REPEAT_ENTRY = CompoundTriggerRepeatEntry.RESTART_DELAY;

export const RT_TRIGGER_REPEAT_ENTRY_DESCRIPTION =
    'If the trigger event happens again before the message sends:';

export const RepeatEntryDisplay: Record<
    CompoundTriggerRepeatEntry,
    { label: string; helperText: string }
> = {
    [CompoundTriggerRepeatEntry.RESTART_DELAY]: {
        label: 'Restart delay on repeat',
        helperText:
            'The wait starts again from the latest event, e.g. abandoned cart: each new add-to-cart restarts the wait.',
    },
    [CompoundTriggerRepeatEntry.KEEP_FIRST]: {
        label: 'Keep first',
        helperText:
            'Repeats are ignored and the original send time stands, e.g. a one-time milestone event like first purchase.',
    },
};

/**
 * Whether a real-time trigger is sending. Changed only through the set-live /
 * pause endpoints, never by saving the trigger. New triggers start paused.
 */
export enum RealtimeTriggerState {
    LIVE = 'live',
    PAUSED = 'paused',
}

export const RealtimeStateDisplayNames: Record<RealtimeTriggerState, string> = {
    [RealtimeTriggerState.LIVE]: 'Live',
    [RealtimeTriggerState.PAUSED]: 'Paused',
};

export const RT_TRIGGER_LIVE_STATE_HELP_TEXT: string[] = [
    'New triggers start paused. Turn this on to start sending.',
    'Pausing stops new qualifications and cancels sends that come due while paused.',
    'Events from earlier live periods still qualify once the trigger is live again.',
    "Message status doesn't turn the trigger on or off. Only this switch does.",
];

export const RESPONSIVE_FORMULAS_NOT_ENABLED_CODE =
    'RESPONSIVE_FORMULAS_NOT_ENABLED';

export const RT_TRIGGER_NOT_ENABLED_TEXT =
    "Real-time sends aren't enabled for this account, so this trigger can't go live. Contact support@aampe.com for more info.";

export const RT_TRIGGER_DEFINITION_CHANGE_CONFIRM = {
    message: 'Restart this live trigger?',
    description:
        'Changing entry or exit events restarts the trigger. Pending sends are cancelled, and only events after the change will qualify.',
    confirm: 'Save and restart',
};
